import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, Send, X, Loader, Brain, Database, Activity, ClipboardList, MessageSquare, BarChart3, Calendar, Users, FileCheck, FileText } from 'lucide-react';
import { consultarModeloIA, obtenerConsultas, editarConsulta, registrarSugerencia } from '../services/chatbotService';
import { obtenerEvaluacionIdChild, obtenerReporteProgreso, obtenerReporteResumen, obtenerTodosLosNinos } from '../services/evaluacionesService';
import { obtenerActividadesProfesor, obtenerActividadesNino } from '../services/ActividadesService';
import { verificarHijos } from '../services/matriculaService';
import moment from 'moment';
import ReactMarkdown from 'react-markdown';
import { obtenerHistorialPagos, obtenerEgresos } from '../services/FinanzasService';
import { consultarIvyChat } from '../services/ivyChatService';



const Chatbot = ({ user, className }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [iaEnabled, setIaEnabled] = useState(false);
    const [currentTypingIndex, setCurrentTypingIndex] = useState(-1);
    const [displayedText, setDisplayedText] = useState('');
    const [selectedOption, setSelectedOption] = useState('chat');
    const [childrenList, setChildrenList] = useState([]);
    const [selectedChildId, setSelectedChildId] = useState(null);
    const messagesEndRef = useRef(null);
    const typingSpeed = 30;
    const [ninos, setNinos] = useState([]);
    const [ninosFiltrados, setNinosFiltrados] = useState([]);
    const [busqueda, setBusqueda] = useState('');
    const [isSearchingChildren, setIsSearchingChildren] = useState(false);
        // Nuevos estados requeridos
    const [chatContext, setChatContext] = useState([]); // Contexto de conversación IA
    const [isFirstInteraction, setIsFirstInteraction] = useState(true); // Control primer mensaje IA
    const [currentModel, setCurrentModel] = useState(''); // Modelo actual siendo consultado

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (isOpen && messages.length === 0 && !iaEnabled) {
            showMainOptions();
            scrollToBottom();
        }
    }, [isOpen, iaEnabled]);

    useEffect(() => {
        if (currentTypingIndex >= 0 && currentTypingIndex < messages.length) {
            const message = messages[currentTypingIndex];
            if (message.type === 'bot') {
                let index = 0;
                const text = message.content;
                
                const typingInterval = setInterval(() => {
                    if (index <= text.length) {
                        setDisplayedText(text.slice(0, index));
                        index++;
                    } else {
                        clearInterval(typingInterval);
                        setCurrentTypingIndex(-1);
                        setDisplayedText('');
                    }
                }, typingSpeed);

                return () => clearInterval(typingInterval);
            }
        }
    }, [currentTypingIndex, messages]);

    const getRoleBasedOptions = () => {
        switch (user.rol) {
            case 'Administrador':
                return [
                    { label: 'Preguntas Frecuentes', value: 'faq', icon: <MessageSquare className="w-5 h-5" />, description: 'Consultas y ayuda general' },
                    { label: 'Reportes y Análisis', value: 'reports', icon: <BarChart3 className="w-5 h-5" />, description: 'Estadísticas y métricas del sistema' },
                    { label: 'Enviar Sugerencia', value: 'suggestion', icon: <MessageCircle className="w-5 h-5" />, description: 'Enviar una sugerencia para mejorar el sistema' }
                ];
            case 'Profesor':
                return [
                    { label: 'Preguntas Frecuentes', value: 'faq', icon: <ClipboardList className="w-5 h-5" />, description: 'Consulta preguntas frecuentes' },
                    { label: 'Actividades Educativas', value: 'activities', icon: <Activity className="w-5 h-5" />, description: 'Gestionar actividades de clase' },
                    { label: 'Evaluaciones', value: 'evaluations', icon: <FileCheck className="w-5 h-5" />, description: 'Visualizar reportes de evaluaciones' },
                    { label: 'Enviar Sugerencia', value: 'suggestion', icon: <MessageCircle className="w-5 h-5" />, description: 'Enviar una sugerencia para mejorar el sistema' }
                ];
            case 'Padre':
                const hijoSeleccionado = JSON.parse(localStorage.getItem('hijoSeleccionado'));
                return [
                    { label: 'Preguntas Frecuentes', value: 'faq', icon: <ClipboardList className="w-5 h-5" />, description: 'Consulta preguntas frecuentes' },
                    { label: 'Actividades de mi hijo', value: 'activities', icon: <Activity className="w-5 h-5" />, description: 'Ver actividades asignadas' },
                    { label: 'Evaluaciones de mi hijo', value: 'evaluations', icon: <FileCheck className="w-5 h-5" />, description: 'Ver evaluaciones y progreso' },
                    { label: 'Enviar Sugerencia', value: 'suggestion', icon: <MessageCircle className="w-5 h-5" />, description: 'Enviar una sugerencia para mejorar el sistema' }
                ];
            default:
                return [
                    { label: 'Chat de Ayuda', value: 'chat', icon: <MessageSquare className="w-5 h-5" />, description: 'Consultas generales' },
                    { label: 'Enviar Sugerencia', value: 'suggestion', icon: <MessageCircle className="w-5 h-5" />, description: 'Enviar una sugerencia para mejorar el sistema' }
                ];
        }
    };
    
    const showMainOptions = () => {
        if (!iaEnabled) {
            setMessages([
                {
                    type: 'bot',
                    content: `¡Hola! 👋 Soy tu asistente virtual. ${
                        user.rol === 'Administrador' 
                            ? 'Puedo ayudar a:'
                            : user.rol === 'Profesor'
                            ? 'Puedo ayudar a:'
                            : 'Puedo ayudarte con:'
                    }`,
                    options: getRoleBasedOptions()
                }
            ]);
        }
    };

    useEffect(() => {
        if (isOpen && messages.length === 0 && user.rol === 'Administrador' && !iaEnabled) {
            showMainOptions();
        }
    }, [isOpen, user.rol, iaEnabled]);

    const getOptionResponse = async (option) => {
        if (option === 'faq' && (user.rol === 'Profesor' || user.rol === 'Padre' || user.rol === 'Administrador')) {
            try {
                const consultasResponse = await obtenerConsultas();
                if (!consultasResponse.success) {
                    throw new Error('No se pudieron obtener las preguntas frecuentes');
                }
        
                const preguntasFrecuentes = consultasResponse.data
                    .filter(consulta => consulta.rol === user.rol)
                    .sort((a, b) => (b.incidencia || 0) - (a.incidencia || 0));
        
                return {
                    content: 'Selecciona una pregunta frecuente:',
                    options: preguntasFrecuentes.map(consulta => ({
                        label: consulta.pregunta,
                        value: `faq_${consulta.id_consulta}`,
                        icon: <MessageSquare className="w-5 h-5" />,
                        description: 'Selecciona para ver la respuesta'
                    }))
                };
            } catch (error) {
                console.error('Error al obtener preguntas frecuentes:', error);
                return {
                    content: 'No hay preguntas frecuentes disponibles en este momento',
                    options: []
                };
            }
        }

        if (option === 'suggestion') {
            return {
                content: 'Por favor, escribe tu sugerencia para mejorar el sistema. Será revisada por los administradores:',
                isSuggestion: true,
                options: []
            };
        }

        if (option.startsWith('faq_') && (user.rol === 'Profesor' || user.rol === 'Padre'|| user.rol === 'Administrador')) {
            try {
                const idConsulta = parseInt(option.split('_')[1], 10);
                
                const consultasResponse = await obtenerConsultas();
                if (!consultasResponse.success) {
                    throw new Error('No se pudieron obtener las consultas');
                }
        
                const consultaActual = consultasResponse.data.find(
                    item => item.id_consulta === idConsulta
                );
        
                if (!consultaActual) {
                    throw new Error('Pregunta no encontrada');
                }
        
                // Incrementar la incidencia
                const nuevaIncidencia = parseInt(consultaActual.incidencia, 10) + 1;
        
                // Obtener el ID del centro desde el localStorage o el contexto del usuario
                const idCentro = JSON.parse(localStorage.getItem('user')).id_centro;
        
                // Editar la consulta con la nueva incidencia
                await editarConsulta(
                    consultaActual.id_consulta,
                    consultaActual.pregunta,
                    consultaActual.respuesta,
                    idCentro,
                    consultaActual.rol,
                    nuevaIncidencia  // Pasa la nueva incidencia incrementada
                );
        
                return {
                    content: (
                        <div className="p-4 bg-gray-100 rounded-lg shadow-md">
                            <h4 className="font-bold text-lg mb-4">📝 Respuesta:</h4>
                            <p>{consultaActual.respuesta}</p>
                        </div>
                    ),
                    options: [
                        {
                            label: 'Ver más preguntas frecuentes',
                            value: 'faq',
                            icon: <MessageSquare className="w-5 h-5" />,
                            description: 'Consultar otras preguntas frecuentes'
                        }
                    ]
                };
            } catch (error) {
                console.error('Error al procesar consulta:', error);
                return {
                    content: 'No se pudo obtener la información solicitada',
                    options: [
                        {
                            label: 'Volver a preguntas frecuentes',
                            value: 'faq',
                            icon: <MessageSquare className="w-5 h-5" />,
                            description: 'Regresar a preguntas frecuentes'
                        }
                    ]
                };
            }
        }
        

        if (option === 'activities' && user.rol === 'Padre') {
            try {
                // Obtener el ID del padre del localStorage
                const padreId = JSON.parse(localStorage.getItem('user'))?.id;
                if (!padreId) {
                    return {
                        content: 'No se pudo acceder a la información del usuario',
                        options: []
                    };
                }
                
                // Usar verificarHijos como lo hace el dashboard
                const response = await verificarHijos(padreId);
                if (!response || !response.hijos || response.hijos.length === 0) {
                    return {
                        content: 'No se encontraron niños registrados para tu cuenta',
                        options: []
                    };
                }
                
                // Filtrar solo los niños con matrícula activa
                const hijosActivos = response.hijos.filter(hijo => hijo.estado_matricula === 'Activa');
                
                if (hijosActivos.length === 0) {
                    return {
                        content: 'No tienes niños con matrícula activa en el sistema',
                        options: []
                    };
                }
                
                return {
                    content: 'Selecciona un hijo para ver sus actividades:',
                    options: hijosActivos.map(hijo => {
                        return {
                            label: `${hijo.nombre} ${hijo.apellido}`,
                            value: `child_activities_${hijo.id_child}`,
                            icon: <Users className="w-5 h-5" />,
                            description: `Ver actividades de ${hijo.nombre}`
                        };
                    })
                };
            } catch (error) {
                console.error('Error al cargar niños:', error);
                return {
                    content: 'No se encontraron registros de niños disponibles',
                    options: []
                };
            }
        }
        
        // Manejar la selección de un niño para ver sus actividades
        if (option.startsWith('child_activities_') && user.rol === 'Padre') {
            try {
                const childId = option.split('child_activities_')[1];
                // Obtener los datos del niño seleccionado
                const responseNinos = await obtenerTodosLosNinos();
                const ninoSeleccionado = responseNinos.data.find(nino => nino.id_child.toString() === childId);
                
                if (!ninoSeleccionado) {
                    return {
                        content: 'No se encontró información del niño seleccionado',
                        options: []
                    };
                }
                
                // Guardar temporalmente el niño seleccionado en localStorage para otras funciones
                localStorage.setItem('hijoSeleccionado', JSON.stringify(ninoSeleccionado));
                
                const response = await obtenerActividadesNino(childId);
                if (!response || !response.actividades || response.actividades.length === 0) {
                    return {
                        content: `No hay actividades registradas para ${ninoSeleccionado.nombre}`,
                        options: [{
                            label: 'Seleccionar otro niño',
                            value: 'activities',
                            icon: <Users className="w-5 h-5" />,
                            description: 'Volver a la lista de niños'
                        }]
                    };
                }
                
                // Agrupar actividades por fecha
                const actividadesPorFecha = {};
                response.actividades.forEach(actividad => {
                    const fecha = moment(actividad.fecha_programada).format('YYYY-MM-DD');
                    if (!actividadesPorFecha[fecha]) {
                        actividadesPorFecha[fecha] = [];
                    }
                    actividadesPorFecha[fecha].push(actividad);
                });
        
                // Crear opciones por fecha
                const opciones = Object.entries(actividadesPorFecha).map(([fecha, actividades]) => ({
                    label: `${moment(fecha).format('DD/MM/YYYY')} (${actividades.length} ${actividades.length === 1 ? 'actividad' : 'actividades'})`,
                    value: `activity_date_${fecha}`,
                    icon: <Activity className="w-5 h-5" />,
                    description: 'Ver actividades de este día'
                }));
                
                // Añadir opción para seleccionar otro niño
                opciones.push({
                    label: 'Seleccionar otro niño',
                    value: 'activities',
                    icon: <Users className="w-5 h-5" />,
                    description: 'Volver a la lista de niños'
                });
        
                return {
                    content: `Actividades de ${ninoSeleccionado.nombre}:`,
                    options: opciones
                };
            } catch (error) {
                console.error('Error al cargar actividades:', error);
                return {
                    content: 'No hay actividades disponibles en este momento',
                    options: []
                };
            }
        }

        if (option.startsWith('activity_date_') && user.rol === 'Padre') {
            try {
                const fecha = option.split('activity_date_')[1];
                const hijoSeleccionado = JSON.parse(localStorage.getItem('hijoSeleccionado'));
                const response = await obtenerActividadesNino(hijoSeleccionado.id_child);
                console.log(response)
                const actividadesDia = response.actividades.filter(actividad => 
                    moment(actividad.fecha_programada).format('YYYY-MM-DD') === fecha
                );
        
                if (actividadesDia.length === 0) {
                    return {
                        content: 'No hay actividades para esta fecha',
                        options: []
                    };
                }
        
                return {
                    content: `Actividades para el ${moment(fecha).format('DD/MM/YYYY')}:`,
                    options: actividadesDia.map(actividad => ({
                        label: actividad.nombre,
                        value: `activity_detail_${actividad.id_actividad}`,
                        icon: <Activity className="w-5 h-5" />,
                        description: truncarDescripcion(actividad.descripcion, 30)
                    }))
                };
            } catch (error) {
                console.error('Error al cargar actividades del día:', error);
                return {
                    content: 'No hay actividades registradas para este día',
                    options: []
                };
            }
        }
if (option.startsWith('activity_detail_') && user.rol === 'Padre') {
    try {
        // Obtener el ID de la actividad desde la opción
        const actividadId = option.split('activity_detail_')[1];
        // Obtener los datos del niño seleccionado desde el localStorage
        const hijoSeleccionado = JSON.parse(localStorage.getItem('hijoSeleccionado'));

        if (!hijoSeleccionado || !hijoSeleccionado.id_child) {
            console.error('No se encontró información del niño seleccionado.');
            return {
                content: 'No se encontró información del niño seleccionado.',
                options: []
            };
        }

        // Llamar a la función para obtener las actividades del niño
        const response = await obtenerActividadesNino(hijoSeleccionado.id_child);

        // Depuración: mostrar la respuesta obtenida
        console.log('Datos de actividades recuperados:', response);

        // Validar que la respuesta contenga actividades
        if (!response || !response.actividades || response.actividades.length === 0) {
            console.warn('No se encontraron actividades para este niño.');
            return {
                content: 'No se encontraron actividades para este niño.',
                options: []
            };
        }

        // Buscar la actividad específica por ID
        const actividad = response.actividades.find(
            act => act.id_actividad.toString() === actividadId
        );

        // Verificar si la actividad fue encontrada
        if (!actividad) {
            console.warn('No se encontró la actividad especificada.');
            return {
                content: 'No se encontró la actividad',
                options: []
            };
        }

        // Diseño del mensaje con los detalles de la actividad
        const mensajeActividad = (
            <div className="p-4 bg-white rounded-lg shadow-md">
                <h4 className="font-bold text-lg">📝 Detalles de la Actividad</h4>
                <p><span className="font-semibold">Nombre:</span> {actividad.nombre}</p>
                <p><span className="font-semibold">Fecha:</span> {moment(actividad.fecha_programada).format('DD/MM/YYYY')}</p>
                <p><span className="font-semibold">Descripción:</span> {actividad.descripcion}</p>
                <p>
                    <span className="font-semibold">Estado:</span>{' '}
                    {actividad.estado === 'completada' ? '✅ Completada' : '⏳ Pendiente'}
                </p>

            </div>
        );

        // Retornar el contenido del mensaje y las opciones adicionales
        return {
            content: mensajeActividad,
            options: [
                {
                    label: 'Ver todas las actividades',
                    value: 'activities',
                    icon: <Activity className="w-5 h-5" />,
                    description: 'Volver a la lista de actividades'
                }
            ]
        };
    } catch (error) {
        // Manejo de errores
        console.error('Error al cargar los detalles de la actividad:', error.message);
        return {
            content: 'Hubo un problema al cargar los detalles de la actividad. Por favor, intenta de nuevo.',
            options: []
        };
    }
}

        if (option === 'evaluations' && user.rol === 'Padre') {
            try {
                // Obtener el ID del padre del localStorage
                const padreId = JSON.parse(localStorage.getItem('user'))?.id;
                if (!padreId) {
                    return {
                        content: 'No se pudo acceder a la información del usuario',
                        options: []
                    };
                }
                
                // Usar verificarHijos como lo hace el dashboard
                const response = await verificarHijos(padreId);
                if (!response || !response.hijos || response.hijos.length === 0) {
                    return {
                        content: 'No se encontraron niños registrados para tu cuenta',
                        options: []
                    };
                }
                
                // Filtrar solo los niños con matrícula activa
                const hijosActivos = response.hijos.filter(hijo => hijo.estado_matricula === 'Activa');
                
                if (hijosActivos.length === 0) {
                    return {
                        content: 'No tienes niños con matrícula activa en el sistema',
                        options: []
                    };
                }
                
                return {
                    content: 'Selecciona un hijo para ver sus evaluaciones:',
                    options: hijosActivos.map(hijo => {
                        return {
                            label: `${hijo.nombre} ${hijo.apellido}`,
                            value: `child_evaluations_${hijo.id_child}`,
                            icon: <ClipboardList className="w-5 h-5" />,
                            description: `Ver evaluaciones de ${hijo.nombre}`
                        };
                    })
                };
            } catch (error) {
                console.error('Error al cargar niños:', error);
                return {
                    content: 'No se encontraron registros de niños disponibles',
                    options: []
                };
            }
        }
        
        // Manejar la selección de un niño para ver sus evaluaciones
        if (option.startsWith('child_evaluations_') && user.rol === 'Padre') {
            try {
                const childId = option.split('child_evaluations_')[1];
                // Obtener los datos del niño seleccionado
                const responseNinos = await obtenerTodosLosNinos();
                const ninoSeleccionado = responseNinos.data.find(nino => nino.id_child.toString() === childId);
                
                if (!ninoSeleccionado) {
                    return {
                        content: 'No se encontró información del niño seleccionado',
                        options: []
                    };
                }
                
                // Guardar temporalmente el niño seleccionado en localStorage para otras funciones
                localStorage.setItem('hijoSeleccionado', JSON.stringify(ninoSeleccionado));
                
                const evaluacionResponse = await obtenerEvaluacionIdChild(childId);
                if (!evaluacionResponse.success || !evaluacionResponse.data || evaluacionResponse.data.length === 0) {
                    return {
                        content: `❌ No hay evaluaciones registradas para ${ninoSeleccionado.nombre}. Por favor, contacta con el personal docente o administrativo para más información.`,
                        options: [{
                            label: 'Seleccionar otro niño',
                            value: 'evaluations',
                            icon: <Users className="w-5 h-5" />,
                            description: 'Volver a la lista de niños'
                        }]
                    };
                }

                // Crear opciones de evaluaciones
                const opciones = evaluacionResponse.data.map(evaluacion => ({
                    label: `Evaluación del ${evaluacion.fecha}`,
                    value: `evaluation_detail_${evaluacion.id_evaluacion}`,
                    icon: <ClipboardList className="w-5 h-5" />,
                    description: 'Ver detalles de la evaluación'
                }));
                
                // Añadir opción para seleccionar otro niño
                opciones.push({
                    label: 'Seleccionar otro niño',
                    value: 'evaluations',
                    icon: <Users className="w-5 h-5" />,
                    description: 'Volver a la lista de niños'
                });
                
                return {
                    content: `Evaluaciones de ${ninoSeleccionado.nombre}:`,
                    options: opciones
                };
            } catch (error) {
                console.error('Error al cargar evaluaciones:', error);
                return {
                    content: 'No hay evaluaciones disponibles en este momento',
                    options: []
                };
            }
        }

        if (option.startsWith('evaluation_detail_') && (user.rol === 'Padre' || user.rol === 'Profesor')) {
            try {
                const evaluationId = option.split('evaluation_detail_')[1];
                const [reporteProgreso, reporteResumen] = await Promise.all([
                    obtenerReporteProgreso(evaluationId),
                    obtenerReporteResumen(evaluationId)
                ]);

                const formatearReporte = (reporte) => {
                    if (!reporte || !reporte.data) return '❌ No hay datos disponibles para este reporte. Por favor, contacta con el personal administrativo para más información.';
                    
                    // Asegurarnos de que reporte.data sea un array
                    const datos = Array.isArray(reporte.data) ? reporte.data : [reporte.data];
                    
                    return datos.map(item => {
                        let porcentaje = 0;
                        let nombre = '';
                        
                        // Manejar diferentes estructuras de datos
                        if (item.porcentaje_ambito) {
                            porcentaje = parseFloat(item.porcentaje_ambito);
                            nombre = item.ambito_aprendizaje;
                        } else if (item.porcentaje_total) {
                            porcentaje = parseFloat(item.porcentaje_total);
                            nombre = 'Total';
                        } else if (item.porcentaje) {
                            porcentaje = parseFloat(item.porcentaje);
                            nombre = item.nombre_ambito || item.ambito;
                        }
                        
                        const nivel = determinarNivelLogro(porcentaje);
                        return `${nombre}: ${porcentaje.toFixed(1)}% - ${nivel}`;
                    }).join('\n');
                };

                const reporteContent = (
                    <div className="space-y-4">
                        <div className="bg-blue-50 p-4 rounded-lg">
                            <h4 className="font-bold text-blue-800 mb-2">📊 Reporte de Progreso</h4>
                            <pre className="whitespace-pre-wrap text-sm text-blue-700">
                                {formatearReporte(reporteProgreso)}
                            </pre>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg">
                            <h4 className="font-bold text-green-800 mb-2">📋 Resumen de Evaluación</h4>
                            <pre className="whitespace-pre-wrap text-sm text-green-700">
                                {formatearReporte(reporteResumen)}
                            </pre>
                        </div>
                    </div>
                );

                return {
                    content: reporteContent,
                    options: [
                        {
                            label: user.rol === 'Padre' ? 'Ver más evaluaciones' : 'Buscar otra evaluación',
                            value: 'evaluations',
                            icon: <ClipboardList className="w-5 h-5" />,
                            description: user.rol === 'Padre' ? 'Volver a la lista de evaluaciones' : 'Buscar evaluaciones de otro niño'
                        }
                    ]
                };
            } catch (error) {
                console.error('Error al obtener reportes:', error);
                return {
                    content: 'No hay reportes de evaluación disponibles',
                    options: [
                        {
                            label: 'Intentar nuevamente',
                            value: 'evaluations',
                            icon: <ClipboardList className="w-5 h-5" />,
                            description: 'Volver a intentar ver las evaluaciones'
                        }
                    ]
                };
            }
        }

        if (option === 'activities' && user.rol === 'Profesor') {
            try {
                const userId = JSON.parse(localStorage.getItem('user')).id;
                const response = await obtenerActividadesProfesor(userId);
                
                if (!response.actividades || response.actividades.length === 0) {
                    return {
                        content: '❌ No hay actividades registradas en el sistema. Si necesitas ayuda para registrar actividades, contacta con el personal administrativo.',
                        options: []
                    };
                }

                // Primero recolectamos los datos de los niños por cada combinación de nombre y estado
                const ninosPorActividad = {};
                response.actividades.forEach(actividad => {
                    const key = `${actividad.nombre}_${actividad.estado}`;
                    if (!ninosPorActividad[key]) {
                        ninosPorActividad[key] = new Set();
                    }
                    if (actividad.detallesNino) {
                        ninosPorActividad[key].add(actividad.detallesNino);
                    }
                });

                // Agrupar actividades por nombre, fecha y estado
                const actividadesAgrupadas = {};
                response.actividades.forEach(actividad => {
                    const nombreActividad = actividad.nombre;
                    const fecha = moment(actividad.fecha_programada).format('YYYY-MM-DD');
                    const estado = actividad.estado;

                    if (!actividadesAgrupadas[nombreActividad]) {
                        actividadesAgrupadas[nombreActividad] = {};
                    }

                    if (!actividadesAgrupadas[nombreActividad][estado]) {
                        actividadesAgrupadas[nombreActividad][estado] = {
                            fechas: new Set(),
                            ninos: ninosPorActividad[`${nombreActividad}_${estado}`] || new Set(), // Usamos los niños recolectados
                            descripcion: actividad.descripcion,
                            id_actividad: actividad.id_actividad
                        };
                    }

                    actividadesAgrupadas[nombreActividad][estado].fechas.add(fecha);
                });

                // Crear opciones para mostrar
                const opciones = [];
                for (const [nombre, estados] of Object.entries(actividadesAgrupadas)) {
                    for (const [estado, detalles] of Object.entries(estados)) {
                        const nombresUnicos = Array.from(detalles.ninos).map(nino => `${nino.nombre} ${nino.apellido}`);
                        const nombresSinRepetir = [...new Set(nombresUnicos)];
                        opciones.push({
                            label: `${nombre} - Estado: ${estado} - Días: ${Array.from(detalles.fechas).join(', ')} (${nombresSinRepetir.length} niño${nombresSinRepetir.length > 1 ? 's' : ''})`,
                            value: `activity_detail_${detalles.id_actividad}`,
                            icon: <Activity className="w-5 h-5" />,
                            description: truncarDescripcion(detalles.descripcion, 30)
                        });
                    }
                }

                return {
                    content: 'Actividades educativas:',
                    options: opciones
                };
            } catch (error) {
                console.error('Error al cargar actividades:', error);
                return {
                    content: 'No hay actividades disponibles en este momento',
                    options: []
                };
            }
        }
        if (option.startsWith('activity_detail_')) {
            try {
                const actividadId = option.split('activity_detail_')[1];
                const response = await obtenerActividadesProfesor(JSON.parse(localStorage.getItem('user')).id);
                
                // Encontrar la actividad específica
                const actividad = response.actividades.find(act => 
                    act.id_actividad.toString() === actividadId
                );
                
                if (!actividad) {
                    return {
                        content: 'No se encontró la actividad',
                        options: []
                    };
                }

                // Obtener todos los niños de todas las actividades que comparten el mismo nombre y estado
                const ninosEnActividad = response.actividades
                    .filter(act => 
                        act.nombre === actividad.nombre && 
                        act.estado === actividad.estado
                    )
                    .flatMap(act => act.detallesNino) // Cambiado a flatMap para obtener todos los niños

                const estadosEmoji = {
                    'completada': '✅',
                    'pendiente': '⏳'
                };

                const mensajeDetalle = (
                    <div className="p-4 bg-white rounded-lg shadow-md">
                        <h4 className="font-bold text-lg mb-4">📝 Detalles de la Actividad</h4>
                        
                        {/* Información de la Actividad */}
                        <div className="mb-4">
                            <p className="mb-2"><span className="font-semibold">Nombre:</span> {actividad.nombre}</p>
                            <p className="mb-2"><span className="font-semibold">Fecha:</span> {moment(actividad.fecha_programada).format('DD/MM/YYYY')}</p>
                            <p className="mb-2"><span className="font-semibold">Descripción:</span> {actividad.descripcion}</p>
                            <p className="mb-2"><span className="font-semibold">Estado:</span> {estadosEmoji[actividad.estado]} {actividad.estado}</p>
                        </div>

                        {/* Lista de Niños */}
                        <div className="bg-gray-50 p-3 rounded-md mb-4">
                            <h5 className="font-bold text-md mb-2">👥 Niños participantes ({ninosEnActividad.length})</h5>
                            <div className="space-y-2">
                                {Array.from(new Set(ninosEnActividad.map(nino => `${nino.nombre} ${nino.apellidos}`))).map((nombre, index) => (
                                    <div key={index} className="p-2 bg-white rounded border border-gray-100">
                                        <p className="mb-1">
                                            <span className="font-semibold">Nombre:</span> {nombre}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Observaciones si existen */}
                        {actividad.observaciones && (
                            <div className="mt-3">
                                <p><span className="font-semibold">Observaciones:</span> {actividad.observaciones}</p>
                            </div>
                        )}
                    </div>
                );

                return {
                    content: mensajeDetalle,
                    options: [
                        {
                            label: 'Ver más actividades',
                            value: 'activities',
                            icon: <Activity className="w-5 h-5" />,
                            description: 'Volver a la lista de actividades'
                        }
                    ]
                };
            } catch (error) {
                console.error('Error al cargar detalles de actividad:', error);
                return {
                    content: 'No se pudieron obtener los detalles de la actividad',
                    options: []
                };
            }
        }

        if (option === 'reports' && user.rol === 'Administrador') {
            try {
                const [ingresosData, egresosData] = await Promise.all([
                    obtenerHistorialPagos(),
                    obtenerEgresos()
                ]);

                const mesActual = new Date().getMonth() + 1;
                const anioActual = new Date().getFullYear();

                const datosFiltrados = {
                    ingresos: ingresosData.historialPagos.filter(i => i.mes_pagado === mesActual && i.anio_pagado === anioActual),
                    egresos: egresosData.egresos.filter(e => {
                        const fecha = new Date(e.fecha_pago);
                        return fecha.getMonth() + 1 === mesActual && fecha.getFullYear() === anioActual;
                    })
                };

                const resumenMensual = {
                    totalIngresos: datosFiltrados.ingresos.reduce((sum, i) => sum + parseFloat(i.monto_pagado), 0),
                    totalEgresos: datosFiltrados.egresos.reduce((sum, e) => sum + parseFloat(e.monto), 0)
                };

                const balance = resumenMensual.totalIngresos - resumenMensual.totalEgresos;
                const egresosPendientes = egresosData.egresos.filter(e => e.estado === 'Pendiente');

                const contenidoReporte = (
                    <div className="space-y-4 max-w-full overflow-hidden">
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">📊 Resumen Financiero del Mes</h3>
                            
                            <div className="space-y-3">
                                <div className="bg-green-50 p-3 rounded-lg">
                                    <p className="text-sm text-green-600 font-medium">Ingresos Totales</p>
                                    <p className="text-lg font-bold text-green-700">${resumenMensual.totalIngresos.toFixed(2)}</p>
                                </div>
                                
                                <div className="bg-red-50 p-3 rounded-lg">
                                    <p className="text-sm text-red-600 font-medium">Egresos Totales</p>
                                    <p className="text-lg font-bold text-red-700">${resumenMensual.totalEgresos.toFixed(2)}</p>
                                </div>
                                
                                <div className={`p-3 rounded-lg ${balance >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
                                    <p className={`text-sm font-medium ${balance >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Balance Final</p>
                                    <p className={`text-lg font-bold ${balance >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                                        ${balance.toFixed(2)}
                                    </p>
                                </div>
                            </div>

                            {egresosPendientes.length > 0 && (
                                <div className="mt-4">
                                    <h4 className="font-medium text-gray-700 mb-2">⚠️ Egresos Pendientes</h4>
                                    <div className="bg-yellow-50 p-3 rounded-lg">
                                        <ul className="space-y-2">
                                            {egresosPendientes.slice(0, 3).map((egreso, index) => (
                                                <li key={index} className="text-sm text-yellow-800 flex justify-between items-center">
                                                    <span>{egreso.concepto}</span>
                                                    <span className="font-medium">${parseFloat(egreso.monto).toFixed(2)}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );

                return {
                    content: contenidoReporte,
                    options: [
                        {
                            label: 'Ver reporte completo',
                            value: 'download_report',
                            icon: <FileText className="w-5 h-5" />,
                            description: 'Acceder al panel de reportes financieros'
                        }
                    ]
                };
            } catch (error) {
                console.error('Error al obtener datos financieros:', error);
                return {
                    content: 'No hay reportes financieros disponibles en este momento',
                    options: []
                };
            }
        }

        if (option === 'download_report') {
            try {
                // Redirigir al usuario a la página de reportes financieros
                window.location.href = '/admin/finanzas/reportes';
                return {
                    content: 'Redirigiendo a la página de reportes financieros...',
                    options: []
                };
            } catch (error) {
                console.error('Error al redirigir:', error);
                return {
                    content: 'No se pudo acceder a los reportes en este momento',
                    options: []
                };
            }
        }

        if (option === 'evaluations' && user.rol === 'Profesor') {
            setIsSearchingChildren(true);
            setBusqueda('');
            return {
                content: 'Por favor, ingresa el nombre del niño para buscar sus evaluaciones:',
                options: []
            };
        }

        if (option.startsWith('child_') && user.rol === 'Profesor') {
            try {
                const childId = option.split('child_')[1];
                const evaluacionResponse = await obtenerEvaluacionIdChild(childId);
                
                if (!evaluacionResponse.success || !evaluacionResponse.data || evaluacionResponse.data.length === 0) {
                    return {
                        content: '❌ No hay evaluaciones registradas para este niño. Por favor, asegúrate de registrar las evaluaciones o contacta con el personal administrativo si necesitas ayuda.',
                        options: [
                            {
                                label: 'Buscar otro niño',
                                value: 'evaluations',
                                icon: <ClipboardList className="w-5 h-5" />,
                                description: 'Buscar evaluaciones de otro niño'
                            }
                        ]
                    };
                }

                return {
                    content: 'Selecciona una evaluación para ver el reporte:',
                    options: evaluacionResponse.data.map(evaluacion => ({
                        label: `Evaluación del ${evaluacion.fecha}`,
                        value: `evaluation_detail_${evaluacion.id_evaluacion}`,
                        icon: <ClipboardList className="w-5 h-5" />,
                        description: 'Ver detalles de la evaluación'
                    }))
                };
            } catch (error) {
                console.error('Error al obtener evaluaciones:', error);
                return {
                    content: 'No hay evaluaciones disponibles en este momento',
                    options: []
                };
            }
        }

        const responses = {
            training: {
                content: 'Selecciona una opción:',
                options: [
                    { label: 'Registrar nuevas consultas', value: 'register_training', icon: <Activity className="w-5 h-5" />, description: 'Agregar nuevas preguntas y respuestas' }
                ]
            },
            register_training: {
                content: 'Por favor, ingresa la pregunta y respuesta separadas por punto y coma (;)\nEjemplo: ¿Cómo estás?;Muy bien, gracias',
                options: []
            },
            activities: {
                content: 'Selecciona una opción para gestionar actividades:',
                options: [
                    { label: 'Ver actividades pendientes', value: 'pending_activities' }
                ]
            },
            childProgress: {
                content: '¿Qué aspecto del desarrollo deseas consultar?',
                options: [
                    { label: 'Desarrollo físico', value: 'physical_development' },
                    { label: 'Desarrollo cognitivo', value: 'cognitive_development' },
                    { label: 'Desarrollo social', value: 'social_development' },
                    { label: 'Reportes de progreso', value: 'progress_reports' }
                ]
            },
            homeActivities: {
                content: 'Actividades disponibles para realizar en casa:',
                options: [
                    { label: 'Actividades de motricidad', value: 'motor_activities' },
                    { label: 'Juegos educativos', value: 'educational_games' },
                    { label: 'Lecturas recomendadas', value: 'recommended_reading' },
                    { label: 'Actividades creativas', value: 'creative_activities' }
                ]
            },
            evaluations: {
                content: 'Gestión de evaluaciones:',
                options: [
                    { label: 'Reportes de evaluaciones', value: 'evaluation_reports' }
                ]
            },
            faq: {
                content: 'Selecciona una pregunta frecuente:',
                options: [
                    { label: '¿Cómo puedo registrar una evaluación de un niño?', value: 'register_evaluation' },
                    { label: 'Horarios de atención', value: 'office_hours' },
                    { label: 'Horarios y precios de las mensualidades disponibles', value: 'tuition_info' }
                ]
            }
        };

        return responses[option] || {
            content: '¿En qué puedo ayudarte?',
            options: []
        };
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const formatResponse = (response) => {
        if (typeof response !== 'string') return response;

        return (
            <div className="markdown-content">
                <ReactMarkdown>{response}</ReactMarkdown>
            </div>
        );
    };

    // Efecto para cargar los niños cuando se activa la búsqueda
    useEffect(() => {
        const cargarNinos = async () => {
            try {
                const response = await obtenerTodosLosNinos();
                if (response.success) {
                    setNinos(response.data);
                }
            } catch (error) {
                console.error('Error al cargar niños:', error);
            }
        };

        if (isSearchingChildren) {
            cargarNinos();
        }
    }, [isSearchingChildren]);

    // Efecto para filtrar niños cuando cambia la búsqueda
    useEffect(() => {
        const filtrarNinos = () => {
            if (busqueda.trim() === '') {
                setNinosFiltrados([]);
            } else {
                setNinosFiltrados(
                    ninos.filter((nino) =>
                        `${nino.nombre} ${nino.apellido}`.toLowerCase().includes(busqueda.toLowerCase())
                    )
                );
            }
        };

        filtrarNinos();
    }, [busqueda, ninos]);

    const handleOptionSelect = async (option) => {
        const selectedOptionLabel = getRoleBasedOptions()
            .find(opt => opt.value === option)?.label || 
            messages[messages.length - 1]?.options?.find(opt => opt.value === option)?.label || 
            'Opción seleccionada';

        setMessages(prev => [...prev, {
            type: 'user',
            content: selectedOptionLabel
        }]);

        setSelectedOption(option);
        
        if (option === 'main') {
            showMainOptions();
            setIsSearchingChildren(false);
            setBusqueda('');
            return;
        }

        const response = await getOptionResponse(option);
        
        setMessages(prev => [...prev, {
            type: 'bot',
            content: response.content,
            isSuggestion: response.isSuggestion || false,
            options: [
                ...(response.options || []),
                {
                    label: 'Volver al menú principal',
                    value: 'main',
                    icon: <X className="w-5 h-5" />,
                    description: 'Regresar a las opciones principales'
                }
            ]
        }]);
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!inputMessage.trim()) return;
    
        const userMessage = inputMessage.trim();
        setInputMessage('');
        setIsLoading(true);
    
        // Eliminar duplicado: solo un setMessages para el usuario
        setMessages(prev => [...prev, { 
            type: 'user', 
            content: userMessage 
        }]);
    
        try {
            const lastBotMessage = messages.find(m => m.type === 'bot' && m.isSuggestion);
            
            if (lastBotMessage && lastBotMessage.isSuggestion) {
                const response = await registrarSugerencia(userMessage);
                
                if (response.success) {
                    setMessages(prev => [...prev, {
                        type: 'bot',
                        content: '¡Gracias! Tu sugerencia ha sido registrada exitosamente. Será revisada por los administradores.',
                        options: [mainMenuOption]
                    }]);
                } else {
                    throw new Error('Error al registrar la sugerencia');
                }
            } else if (iaEnabled) {
                // Determinar el modelo que se va a usar (análisis previo simple)
                const mensajeLower = userMessage.toLowerCase();
                const esComplejo = mensajeLower.length > 50 || 
                                   mensajeLower.includes('por qué') || 
                                   mensajeLower.includes('explica') ||
                                   mensajeLower.includes('cómo') ||
                                   mensajeLower.includes('fiebre') ||
                                   mensajeLower.includes('desarrollo');
                
                setCurrentModel(esComplejo ? 'DeepSeek R1 Turbo' : 'Llama 3.1 8B Instruct');
                
                const updatedContext = [
                    ...chatContext,
                    {
                        role: 'user',
                        content: userMessage,
                        metadata: {
                            timestamp: new Date().toISOString(),
                            userRole: user.rol,
                            source: 'chat_interface'
                        }
                    }
                ];
            
                const response = await consultarIvyChat(userMessage, updatedContext, user.rol);
                
                if (response.success && response.data) {
                    const modeloUsado = response.data.modelo || 'IA';
                    const botResponse = {
                        type: 'bot',
                        content: response.data.respuesta,
                        isIA: true,
                        modelo: modeloUsado,
                        options: [mainMenuOption], // Añadir opciones aquí
                        metadata: {
                            model: modeloUsado,
                            responseId: crypto.randomUUID(),
                            timestamp: new Date().toISOString()
                        }
                    };
            
                    setMessages(prev => [...prev, botResponse]);
                    setChatContext([
                        ...updatedContext,
                        {
                            role: 'assistant',
                            content: response.data.respuesta,
                            metadata: {
                                ...botResponse.metadata,
                                tokensUsed: response.data.tokens_used
                            }
                        }
                    ]);
            
                    if (isSearchingChildren) {
                        setIsSearchingChildren(false);
                        setBusqueda('');
                        setNinosFiltrados([]);
                    }
            
                } else {
                    const errorMessage = response.error?.includes('timeout') 
                        ? 'El servicio está ocupado. Intenta nuevamente en unos momentos.' 
                        : 'Error procesando tu consulta con IA. Intenta reformularla.';
            
                    throw new Error(errorMessage);
                }
            }
        } catch (error) {
            console.error('Error:', error);
            setMessages(prev => [...prev, {
                type: 'bot',
                content: error.message || 'No se pudo procesar la solicitud en este momento. Por favor, intenta nuevamente.',
                options: [mainMenuOption],
                isError: true
            }]);
        } finally {
            setIsLoading(false);
            setCurrentModel(''); // Limpiar el modelo actual
        }
    };
    
    // Objeto reutilizable para las opciones
    const mainMenuOption = {
        label: 'Volver al menú principal',
        value: 'main',
        icon: <X className="w-5 h-5" />,
        description: 'Regresar a las opciones principales'
    };
    const determinarNivelLogro = (porcentaje) => {
        if (porcentaje <= 66.00) return "NO LO CONSIGUE ☹️";
        if (porcentaje <= 86.00) return "EN PROCESO 😐";
        return "DOMINA EL LOGRO 😊";
    };

    const truncarDescripcion = (descripcion, limite = 50) => {
        if (!descripcion) return '';
        if (descripcion.length <= limite) return descripcion;
        return descripcion.substring(0, limite) + '...';
    };

    return (
        <div className={`fixed bottom-4 right-4 z-50 ${className || ''}`}>
            {/* Botón flotante para abrir el chatbot (solo visible cuando está cerrado) */}
            {!isOpen && (
                <button
                    onClick={() => setIsOpen(true)}
                    className="bg-gradient-to-r from-blue-600 to-blue-400 hover:from-blue-700 hover:to-blue-500 text-white rounded-full p-4 shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center"
                    aria-label="Abrir asistente virtual"
                >
                    <MessageCircle size={24} />
                </button>
            )}

            {isOpen && (
                <div className="fixed bottom-16 right-0 md:right-4 z-40 animate-fade-in">
                    <div className="w-[95vw] md:w-[400px] max-h-[80vh] bg-white rounded-2xl shadow-2xl border-0 flex flex-col relative animate-slide-up">
                        {/* Header con título y botón de cerrar */}
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                            <div className="flex items-center space-x-2">
                                <div className="bg-blue-500 rounded-full p-1.5">
                                    <MessageCircle size={18} className="text-white" />
                                </div>
                                <h2 className="text-lg font-semibold bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">
                                    Asistente Virtual
                                </h2>
                            </div>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="bg-gray-100 hover:bg-gray-200 text-gray-500 rounded-full p-1.5 transition-all duration-200 flex items-center justify-center"
                                aria-label="Cerrar asistente virtual"
                            >
                                <X size={16} />
                            </button>
                        </div>
{/* Toggle de Modo IA/Soporte */}
<div className="px-5 py-3 bg-gray-50">
    <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700">Selecciona un modo</h3>
        <div className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
            {iaEnabled ? 'Modo IA activo' : 'Modo Soporte activo'}
        </div>
    </div>
    <div className="flex justify-center">
        <div className="relative bg-white rounded-full p-1.5 flex items-center shadow-lg border border-gray-200 w-80">
            <button
                type="button"
                onClick={() => {
                    if (!iaEnabled) {
                        setIaEnabled(true);
                        setMessages([]);
                    }
                }}
                className={`flex items-center justify-center gap-2 z-10 py-2 px-6 w-1/2 rounded-full transition-all duration-300 text-sm ${iaEnabled ? 'text-white font-medium' : 'text-gray-500 hover:text-gray-700'}`}
                title="Modo IA - Chatea con inteligencia artificial"
            >
                <Brain size={15} className={`transition-all duration-200 ${iaEnabled ? 'text-blue-100' : ''}`} />
                <span>IA</span>
            </button>
            
            <button 
                type="button" 
                onClick={() => {
                    if (iaEnabled) { 
                        setIaEnabled(false); 
                        setMessages([]);
                    }
                }}
                className={`flex items-center justify-center gap-2 z-10 py-2 px-6 w-1/2 rounded-full transition-all duration-300 text-sm ${!iaEnabled ? 'text-white font-medium' : 'text-gray-500 hover:text-gray-700'}`}
                title="Modo Soporte - Habla con un asesor"
            >
                <MessageSquare size={15} className={`transition-all duration-200 ${!iaEnabled ? 'text-green-100' : ''}`} />
                <span>Soporte</span>
            </button>
            
            {/* Indicador de selección con colores mejorado */}
            <div 
                className={`absolute top-1.5 bottom-1.5 rounded-full shadow-lg transition-all duration-300 ease-out ${iaEnabled ? 'bg-gradient-to-r from-blue-600 to-blue-500 left-1.5 right-[calc(50%+3px)]' : 'bg-gradient-to-r from-green-600 to-green-500 right-1.5 left-[calc(50%+3px)]'}`}
            ></div>
        </div>
    </div>
</div>
                        
                        <div className="h-[50vh] md:h-96 overflow-y-auto p-3 md:p-6 bg-gray-50 scroll-smooth">
                            {/* Mensaje de bienvenida permanente cuando el modo IA está activo */}
                            {iaEnabled && (
                                <div className="mb-3 md:mb-4 text-left">
                                    <div className="inline-block p-3 md:p-4 rounded-2xl max-w-[85%] shadow-sm bg-gradient-to-r from-blue-600 to-blue-400 text-white">
                                        Hola soy Ivy, tu asistente inteligente del centro infantil. ¿En qué puedo ayudarte hoy?
                                    </div>
                                </div>
                            )}
                            
                            {/* Mensajes de la conversación */}
                            {messages.map((message, index) => (
                                <div
                                    key={index}
                                    className={`mb-3 md:mb-4 ${
                                        message.type === 'user' ? 'text-right' : 'text-left'
                                    }`}
                                >
                                    <div
                                        className={`inline-block p-3 md:p-4 rounded-2xl max-w-[85%] shadow-sm ${
                                            message.type === 'user'
                                                ? 'bg-gradient-to-r from-blue-600 to-blue-400 text-white'
                                                : 'bg-white text-gray-800'
                                        }`}
                                    >
                                        {/* Mostrar modelo usado si es una respuesta de IA */}
                                        {message.type === 'bot' && message.modelo && (
                                            <div className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                                                <Brain size={12} className="inline" />
                                                <span className="font-semibold">{message.modelo}</span>
                                            </div>
                                        )}
                                        {typeof message.content === 'string' ? (
                                            currentTypingIndex === index ? displayedText : formatResponse(message.content)
                                        ) : (
                                            message.content
                                        )}
                                        {currentTypingIndex === index && typeof message.content === 'string' && (
                                            <span className="inline-block w-1.5 h-4 ml-1 bg-current animate-pulse" />
                                        )}
                                        {message.options && !iaEnabled && (
                                            <div className="mt-3 md:mt-4 grid grid-cols-1 gap-2">
                                                {message.options.map((option, idx) => (
                                                    <button
                                                        key={idx}
                                                        onClick={() => handleOptionSelect(option.value)}
                                                        className="flex items-center gap-2 md:gap-3 bg-white p-2 md:p-3 rounded-xl text-left hover:bg-blue-50 transition-colores border border-gray-100 hover:border-blue-200 group text-sm md:text-base"
                                                    >
                                                        <div className="p-1.5 md:p-2 bg-blue-100 rounded-lg text-blue-600 group-hover:bg-blue-200">
                                                            {option.icon}
                                                        </div>
                                                        <div>
                                                            <div className="font-medium text-gray-800">{option.label}</div>
                                                            <div className="text-xs md:text-sm text-gray-500">{option.description}</div>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {isLoading && (
                                <div className="flex flex-col items-center py-3 md:py-4 gap-2">
                                    <div className="text-sm text-blue-600 flex items-center gap-2 animate-pulse">
                                        <Brain size={16} className="animate-pulse" />
                                        <span className="inline-block animate-bounce">Consultando</span>
                                        <span className="flex space-x-1">
                                            <span className="inline-block animate-bounce" style={{ animationDelay: '0.1s' }}>.</span>
                                            <span className="inline-block animate-bounce" style={{ animationDelay: '0.2s' }}>.</span>
                                            <span className="inline-block animate-bounce" style={{ animationDelay: '0.3s' }}>.</span>
                                        </span>
                                    </div>
                                    {currentModel && (
                                        <div className="text-xs text-gray-500 font-medium">
                                            {currentModel}
                                        </div>
                                    )}
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        <form onSubmit={handleSendMessage} className="border-t p-3 md:p-4 bg-white rounded-b-2xl relative">
                            {/* Panel de filtro de niños (sobrepuesto en el chat y expandiendose hacia arriba) */}
                            {isSearchingChildren && ninosFiltrados.length > 0 && (
                                <div className="absolute bottom-full mb-2 left-0 right-0 mx-3 border rounded-lg max-h-36 md:max-h-48 overflow-y-auto bg-white shadow-lg z-10">
                                    <div className="sticky top-0 bg-blue-50 p-2 border-b font-medium text-blue-700 flex justify-between items-center">
                                        <span>Resultados de búsqueda</span>
                                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">{ninosFiltrados.length} encontrados</span>
                                    </div>
                                    <ul>
                                        {ninosFiltrados.map((nino) => (
                                            <li
                                                key={nino.id_child}
                                                className="p-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 flex items-center gap-2"
                                                onClick={() => {
                                                    handleOptionSelect(`child_${nino.id_child}`);
                                                    setIsSearchingChildren(false);
                                                    setBusqueda('');
                                                }}
                                            >
                                                <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold">
                                                    {nino.nombre.charAt(0)}{nino.apellido.charAt(0)}
                                                </div>
                                                <span>{nino.nombre} {nino.apellido}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            
                            {/* Mostrar el cuadro de texto solo cuando sea necesario en modo soporte */}
                            {(iaEnabled || isSearchingChildren || messages[messages.length - 1]?.isSuggestion) && (
                                <div className="flex gap-2 md:gap-3 items-center">
                                    <input
                                        type="text"
                                        value={isSearchingChildren ? busqueda : inputMessage}
                                        onChange={(e) => {
                                            if (isSearchingChildren) {
                                                setBusqueda(e.target.value);
                                            } else {
                                                setInputMessage(e.target.value);
                                            }
                                        }}
                                        placeholder={
                                            isSearchingChildren
                                                ? "Buscar niño..."
                                                : messages[messages.length - 1]?.isSuggestion
                                                    ? "Escribe tu sugerencia..."
                                                    : iaEnabled 
                                                        ? "Pregunta sobre desarrollo infantil..." 
                                                        : "¿Qué necesitas consultar?"
                                        }
                                        className="flex-1 p-2 md:p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 bg-gray-50 text-black text-sm md:text-base"
                                        autoFocus={isSearchingChildren}
                                    />
                                    <button
                                        type="submit"
                                        className="bg-gradient-to-r from-blue-600 to-blue-400 text-white p-2 md:p-3 rounded-xl hover:from-blue-700 hover:to-blue-500 disabled:opacity-50 transition-all duration-200"
                                        disabled={isLoading || !inputMessage.trim()}
                                    >
                                        <Send size={18} className="md:w-5 md:h-5" />
                                    </button>
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Chatbot;